/* eslint-disable max-lines */
// deps
import { useState, useCallback, useEffect, useContext, useRef, useMemo } from "react";

// internal deps
import {
    useMountedRef,
    RouteContext,
    captureError,
    GlobalStoreContextType,
    GlobalStoreContext,
} from "@swiggy-private/core-ui";
import { VALID_UPI_PAYMENT_CODE } from "@swiggy-private/payment-core";
import { Some } from "@swiggy-private/js-fp";
import Blaze from "@swiggy-private/blaze.js";
import { SliderButtonContext } from "@swiggy-private/payment-ui";

// contexts
import { JuspayContext } from "@payment-client/contexts/JuspayContext";
import { AmazonPaySdkContext } from "@payment-client/contexts/AmazonPaySdkContext";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";
import { PhonepeContext } from "@payment-client/contexts/PhonepeSdkContext";

// helpers
import {
    getPlaceOrderParams,
    handlePlaceOrderSuccess,
    handlePlaceOrderFailure,
    canCapturePendingPaymentState,
    allowedMethodAppStateInIdleMode,
} from "@payment-client/helpers/OrderHelper";
import { sendOrderFailedEvent, ORDER_FAIL_INSTANCE } from "@payment-client/helpers/OrderFailedHelper";
import { getReferrerPaymentsPageRoute } from "@payment-client/helpers/PaymentHelper";

// interfaces
import { PlaceOrderSuccessHandlerReturnType } from "@payment-client/interfaces/Order";

// helpers
import { isBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";
import { buildOrderLoadTimePayload } from "@payment-client/helpers/Metrics";

// constants
import {
    PAYMENT_FALLBACK_STATUS_CODE,
    INVALID_COUPON_STATUS_CODE,
    PAST_PAYMENT_ALREADY_SUCCESSFUL_STATUS_CODE,
    DINEOUT_MAX_ITEM_BREACH_STATUS_CODE,
    DINEOUT_INCORRECT_DINECASH_POOLED_AMOUNT_STATUS_CODE,
} from "@payment-client/constants/StatusCodes";
import { ORDER_STATUS } from "@payment-client/constants/Order";

// hooks
import {
    useNavigate,
    CheckoutRoute,
    CheckoutActionTypes,
    getBridgeSdk,
    GlobalHeaderState,
    ConfigContext,
    ApiContext,
    CheckoutContext,
    SwgyHeaderKeysList,
    PlaceOrderData,
    AnalyticsContext,
    recordNREvent,
    NEWRELIC_TABLES,
    BLAZE_EVENTS,
    DineoutCheckoutRoute,
    setQuickCheckoutBackgroundTransparent,
    useQuickCheckoutNavHandlers,
} from "@swiggy-private/checkout-core";

// analytics
import { onPlaceOrderErrorPopupEvent } from "@payment-client/analytics/events/Payment";

// helpers
import { getSwiggyMoneyReceivedRedirectUrl } from "@payment-client/helpers/SwiggyInstrumentsHelper";
import {
    getOrderInterruptionAlertData,
    isOrderInterruptionStatusCode,
} from "@payment-client/helpers/OrderInterruptionHelper";
import { getJuspaySdkFeatureAvailability } from "@payment-client/helpers/JuspayHelpers";
import {
    handlePaymentFailure,
    shouldUpdateQuickCheckoutBackgroundForTurboPlaceOrder,
} from "@payment-client/helpers/PlaceOrderHookHelper";
import { GenericPaymentAlertContext } from "@payment-client/contexts/GenericPaymentAlertContext";
import { getTurboPaymentMethodFromPlaceOrder } from "@payment-client/helpers/TurboPayment";
import { STORAGE_TYPES } from "@swiggy-private/native-bridge";
import { placeOrderPublic } from "@payment-client/apis/publicOrder";

interface PlaceOrderState {
    placeOrderInProgress: boolean;
    placeOrderData?: PlaceOrderData;
}

type UsePlaceOrderReturnType = [
    PlaceOrderState,
    (data: NonNullable<PlaceOrderState["placeOrderData"]>) => void,
    boolean,
];

const usePlaceOrder = (): UsePlaceOrderReturnType => {
    const isMountedRef = useMountedRef();
    const apiContextValue = useContext(ApiContext);
    const { paymentReturnUrl, addSwiggyMoneyReturnUrl } = useContext(ConfigContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const { isSdkAvailable: isJuspaySdkAvailable, featuresAvailability: juspaySdkFeatureAvailable } =
        useContext(JuspayContext);
    const { state: apayState } = useContext(AmazonPaySdkContext);
    const { isPhonepeSdkAvailable } = useContext(PhonepeContext);
    const { billTotal } = useContext(PaymentPayContext);
    const apiCallRef = useRef(false);
    const { setCheckoutState, checkoutState } = useContext(CheckoutContext);
    const { checkoutMetaInfo, paymentResponse, reloadPayments } = useContext(ShowPaymentContext);
    const globalStore = useContext<GlobalStoreContextType>(GlobalStoreContext);
    const { onPostSliderSuccessCallback } = useContext(SliderButtonContext);
    const { handleResetOnPlaceOrderFailure } = useQuickCheckoutNavHandlers();
    const {
        hideCloseIcon,
        hideOnOverlayClick,
        primaryAction: paymentAlertPrimaryAction,
        onClose: paymentAlertOnClose,
    } = useContext(GenericPaymentAlertContext);

    const [placeOrderState, setPlaceOrderState] = useState<PlaceOrderState>({ placeOrderInProgress: false });

    const returnUrl = useMemo(
        () =>
            isBusinessLineSwiggyMoney()
                ? Some(getSwiggyMoneyReceivedRedirectUrl(addSwiggyMoneyReturnUrl?.unwrapOr("")))
                : paymentReturnUrl,
        [addSwiggyMoneyReturnUrl, paymentReturnUrl],
    );

    const doPlaceOrder = useCallback(
        (placeOrderData: PlaceOrderData) => {
            // discarding place order request if any of the following following checkout states are active
            if (
                !isMountedRef.current ||
                placeOrderState.placeOrderInProgress ||
                apiCallRef.current ||
                checkoutState.type === CheckoutActionTypes.PROCESS_ORDER ||
                checkoutState.type === CheckoutActionTypes.VPA_TIMER ||
                checkoutState.type === CheckoutActionTypes.CONFIRM_ORDER
            ) {
                return;
            }
            setPlaceOrderState({ placeOrderInProgress: true, placeOrderData });
        },
        [isMountedRef, placeOrderState.placeOrderInProgress, apiCallRef.current, checkoutState.type],
    );

    // Keep the apiCallRef true until the committed render shows
    // placeOrderInProgress=false. This avoids a duplicate place order if the main effect
    // re-runs in the window between resetting the state commit.
    useEffect(() => {
        if (isMountedRef.current && !placeOrderState.placeOrderInProgress) {
            apiCallRef.current = false;
        }
    }, [isMountedRef, placeOrderState.placeOrderInProgress]);

    const navigateTo = useNavigate();
    const activeRouteName = useContext(RouteContext).route?.name || "";
    const apiHost = apiContextValue.host.unwrap();

    const handlePlaceOrderData = useCallback(
        ({ orderData }: PlaceOrderSuccessHandlerReturnType) => {
            const { placeOrderData } = placeOrderState;

            if (shouldUpdateQuickCheckoutBackgroundForTurboPlaceOrder(placeOrderData, activeRouteName)) {
                // Status 0 — drop transparent bg once place order succeeds.
                setQuickCheckoutBackgroundTransparent(false);
            }

            // This is for debugging on an issue where orderId is being passed incorrectly in confirm order calls
            getBridgeSdk()
                .storage({
                    type: STORAGE_TYPES.SAVE,
                    data: {
                        currentOrderId: String(orderData?.orderId || ""),
                    },
                })
                .catch(() => void 0);
            if (!isMountedRef.current || !placeOrderData) {
                return;
            }

            // update payment in pending state
            if (canCapturePendingPaymentState(placeOrderData) && orderData.pendingPaymentParams) {
                getBridgeSdk()
                    .paymentInPendingState({
                        paymentMethodName: placeOrderData.paymentMethodData.paymentCode,
                        marketplaceBusinessLine:
                            GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine) || "",
                        marketplaceCategory: GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceCategory) || "",
                        requestObj: orderData.pendingPaymentParams,
                    })
                    .catch(() => void 0);
            }

            // Set App state Active in background/idle mode
            if (allowedMethodAppStateInIdleMode(placeOrderData.paymentMethodData.paymentCode)) {
                getBridgeSdk()
                    .setAppStateInIdleMode({ active: true })
                    .catch(() => void 0);
            }

            if (placeOrderData.paymentMethodData.paymentCode.toUpperCase() === VALID_UPI_PAYMENT_CODE.UPI_COLLECT) {
                setCheckoutState({
                    type: CheckoutActionTypes.VPA_TIMER,
                    payload: {
                        order: orderData,
                        placeOrderData,
                        upiCollectStartTime: Date.now(),
                    },
                });
            } else {
                //Checking Juspay SDK availiblity based on feature
                const useJuspaySDK = getJuspaySdkFeatureAvailability(
                    placeOrderData?.paymentMethodData.groupName,
                    isJuspaySdkAvailable,
                    juspaySdkFeatureAvailable,
                );

                Blaze.recordStart(BLAZE_EVENTS.RECORD_PROCESS_ORDER_LOAD_TIME);
                try {
                    // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                        name: "handlePlaceOrderData",
                        orderId: orderData?.orderId || "",
                    });
                } catch {
                    /* No-op*/
                }
                setCheckoutState({
                    type: CheckoutActionTypes.PROCESS_ORDER,
                    payload: {
                        placeOrderData,
                        order: orderData,
                        useJuspaySDK,
                        juspayMeta: paymentResponse?.juspayMeta,
                        apayState: apayState,
                        isPhonepeSdkAvailable: isPhonepeSdkAvailable,
                        startTime: Date.now(),
                    },
                });
            }
        },
        [
            isMountedRef,
            placeOrderState,
            setCheckoutState,
            isJuspaySdkAvailable,
            paymentResponse,
            isPhonepeSdkAvailable,
            apayState,
            juspaySdkFeatureAvailable,
            activeRouteName,
        ],
    );

    const handlePlaceOrderDataError = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err: any) => {
            const { placeOrderData } = placeOrderState;
            if (!isMountedRef.current || !placeOrderData) {
                return;
            }

            if (shouldUpdateQuickCheckoutBackgroundForTurboPlaceOrder(placeOrderData, activeRouteName)) {
                // Failure — restore transparent QC sheet background.
                setQuickCheckoutBackgroundTransparent(true);
            }

            sendOrderFailedEvent({
                totalBill: String(billTotal),
                error: err,
                failInstance: ORDER_FAIL_INSTANCE.PLACE,
                placeOrderData,
            });

            const isTurboPaymentMethod = getTurboPaymentMethodFromPlaceOrder(placeOrderData);

            if (isTurboPaymentMethod) {
                onPostSliderSuccessCallback();
            }
            // record place order failed event to NR
            recordNREvent({
                eventType: NEWRELIC_TABLES.PAYMENT_WEB_PLACE_ORDER_FAILED,
                method: placeOrderData.paymentMethodData.paymentCode || "",
                autopayVariant: err?.data?.xpData?.autoPayVariant || "--",
                statusCode: err?.statusCode || "--",
                isTurboPaymentMethod,
            });
            try {
                //Checking Juspay SDK availiblity based on feature
                const _isJuspaySdkAvailable = getJuspaySdkFeatureAvailability(
                    placeOrderData?.paymentMethodData.groupName,
                    isJuspaySdkAvailable,
                    juspaySdkFeatureAvailable,
                );
                const payload = buildOrderLoadTimePayload({
                    placeOrderData,
                    isTurboPaymentMethod,
                    _isJuspaySdkAvailable,
                });
                // Record the P2O metrics. From place order to successful confirm order for diffrent payment methods
                Blaze.recordEnd(BLAZE_EVENTS.RECORD_ORDER_LOAD_TIME, {
                    ...payload,
                    orderStatus: ORDER_STATUS.FAILED,
                });
            } catch {
                // no-op
            }

            if (placeOrderData?.extraArbitaryData?.["isInAppUpiOnBoardingFlow"] === true) {
                reloadPayments();
            }
            if (isOrderInterruptionStatusCode(err)) {
                const orderInterruptionAlertData = getOrderInterruptionAlertData(err);
                setCheckoutState({
                    type: CheckoutActionTypes.ORDER_INTERRUPTION,
                    payload: {
                        orderInterruptionAlertData,
                        placeOrderData,
                        errorStatusCode: err.statusCode,
                    },
                });
            } else if (err && err.statusCode === PAYMENT_FALLBACK_STATUS_CODE) {
                handlePaymentFailure(err, placeOrderData, setCheckoutState);
            } else if (err && err.statusCode === PAST_PAYMENT_ALREADY_SUCCESSFUL_STATUS_CODE) {
                const interruptData = err?.data;
                setCheckoutState({
                    type: CheckoutActionTypes.PAY_ON_DELIVERY_PAYMENT_ALREADY_DONE,
                    payload: {
                        placeOrderData,
                        interruptData,
                    },
                });
            } else if (err && err.statusCode === INVALID_COUPON_STATUS_CODE) {
                const invalidCouponResponseData = err?.data?.invalidCouponResponse;
                setCheckoutState({
                    type: CheckoutActionTypes.INVALID_COUPON_ALERT,
                    payload: {
                        invalidCouponResponseData,
                        placeOrderData,
                    },
                });
            } else if (
                err &&
                err.statusCode === DINEOUT_MAX_ITEM_BREACH_STATUS_CODE &&
                activeRouteName === CheckoutRoute.STEAL_STORE_CART // requirement is to only show on cart page for max item breach error
            ) {
                // using generic payment alert to show the max item breach error on cart page as data will be null here
                setCheckoutState({
                    type: CheckoutActionTypes.GENERIC_PAYMENT_ALERT,
                    payload: {
                        title: err?.statusTitle || "",
                        message: err?.statusMessage || "",
                        hideIcon: false,
                        hideCloseIcon,
                        hideOnOverlayClick,
                        onClose: paymentAlertOnClose,
                        primaryAction: {
                            text: err?.ctaText,
                            ...paymentAlertPrimaryAction,
                        },
                    },
                });
            } else if (
                err &&
                err.statusCode === DINEOUT_INCORRECT_DINECASH_POOLED_AMOUNT_STATUS_CODE &&
                activeRouteName === DineoutCheckoutRoute.BILL_PAYMENT_CART
            ) {
                setCheckoutState({
                    type: CheckoutActionTypes.GENERIC_PAYMENT_ALERT,
                    payload: {
                        title: err?.statusTitle || "",
                        message: err?.statusMessage || "",
                        hideIcon: false,
                        hideCloseIcon,
                        hideOnOverlayClick,
                        onClose: paymentAlertOnClose,
                        primaryAction: {
                            text: err?.ctaText,
                            ...paymentAlertPrimaryAction,
                        },
                    },
                });
            } else {
                handlePlaceOrderFailure(err, handleResetOnPlaceOrderFailure(placeOrderData, activeRouteName));
                // This is to handle sdk turbo loader flow when place order fails with unhandled status.
                // Otherwise user will get stuck turbo loader screen
                if (placeOrderData?.extraArbitaryData?.isFromTurboPlaceOrder) {
                    setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
                    navigateTo(CheckoutRoute.PAYMENTS, {}, true);
                }
                onPlaceOrderErrorPopupEvent(
                    err.statusCode ?? 1,
                    err.statusMessage ?? "Received error while place order",
                    impressionEvent,
                );
            }
        },
        [
            placeOrderState,
            isMountedRef,
            billTotal,
            onPostSliderSuccessCallback,
            hideCloseIcon,
            hideOnOverlayClick,
            paymentAlertPrimaryAction,
            reloadPayments,
            setCheckoutState,
            impressionEvent,
            navigateTo,
            activeRouteName,
            paymentAlertOnClose,
            handleResetOnPlaceOrderFailure,
        ],
    );

    useEffect(() => {
        if (
            !isMountedRef.current ||
            activeRouteName === CheckoutRoute.PAYMENTS ||
            activeRouteName === CheckoutRoute.SWIGGY_MONEY_PAYMENTS
        ) {
            return;
        }

        if (
            checkoutState.type === CheckoutActionTypes.ORDER_INTERRUPTION ||
            checkoutState.type === CheckoutActionTypes.PAYMENT_FAILED ||
            checkoutState.type === CheckoutActionTypes.INVALID_COUPON_ALERT ||
            checkoutState.type === CheckoutActionTypes.PAY_ON_DELIVERY_PAYMENT_ALREADY_DONE
        ) {
            const referrerRoute = getReferrerPaymentsPageRoute();
            navigateTo(referrerRoute);
        }
    }, [activeRouteName, isMountedRef, navigateTo, checkoutState.type]);

    useEffect(() => {
        // is mounted check..
        if (
            !isMountedRef.current ||
            apiCallRef.current ||
            !placeOrderState.placeOrderInProgress ||
            !placeOrderState.placeOrderData
        ) {
            return;
        }

        // Record the P2O metrics. From place order to successful confirm order
        Blaze.recordStart(BLAZE_EVENTS.RECORD_ORDER_LOAD_TIME);

        // Restore address id from session storage if present
        const savedCartAddressId = globalStore.get(SwgyHeaderKeysList.cartAddressId);
        if (savedCartAddressId) {
            GlobalHeaderState.setHeader(SwgyHeaderKeysList.cartAddressId, savedCartAddressId);
        }

        apiCallRef.current = true;
        const placeOrderData = placeOrderState.placeOrderData;

        //Check SDK availblity Based on group name
        const _isJuspaySdkAvailable = getJuspaySdkFeatureAvailability(
            placeOrderData.paymentMethodData.groupName,
            isJuspaySdkAvailable,
            juspaySdkFeatureAvailable,
        );

        const placeOrderParams = getPlaceOrderParams({
            placeOrderData,
            apayState,
            isJuspaySdkAvailable: _isJuspaySdkAvailable,
            paymentReturnUrl: returnUrl,
            checkoutMetaInfo,
            paymentAmount: paymentResponse?.paymentAmount,
        });

        if (shouldUpdateQuickCheckoutBackgroundForTurboPlaceOrder(placeOrderData, activeRouteName)) {
            // start with transparent bg
            setQuickCheckoutBackgroundTransparent(true);
        }

        placeOrderPublic(apiHost, placeOrderParams, placeOrderData, true)
            .then((res) =>
                handlePlaceOrderSuccess(
                    res,
                    NEWRELIC_TABLES.PAYMENTS_WEB_PLACE_ORDER,
                    placeOrderData || {},
                    _isJuspaySdkAvailable,
                ),
            )
            .then(handlePlaceOrderData)
            .catch(handlePlaceOrderDataError)
            .catch(captureError)
            .then(() => {
                if (isMountedRef.current) {
                    setPlaceOrderState({ placeOrderData, placeOrderInProgress: false });
                }
            });
    }, [
        apayState,
        apiHost,
        checkoutMetaInfo,
        globalStore,
        handlePlaceOrderData,
        handlePlaceOrderDataError,
        isJuspaySdkAvailable,
        isMountedRef,
        juspaySdkFeatureAvailable,
        paymentResponse,
        placeOrderState.placeOrderData,
        placeOrderState.placeOrderInProgress,
        returnUrl,
    ]);

    // for some cases when we call doPlaceOrder the mounted is still false, leading to the function call getting ignore, this way a check can be added to trigger the call only when this hook is mounted.
    return [placeOrderState, doPlaceOrder, isMountedRef.current];
};

export default usePlaceOrder;
